import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSubjectToken } from "../lib/subject-api";
import { useInterviewSession } from "../hooks/useInterviewSession";
import { useQuestionVoice } from "../hooks/useQuestionVoice";
import { useRealtimeTranscription } from "../hooks/useRealtimeTranscription";
import { QuestionAndAnswer } from "../components/interview/QuestionAndAnswer";
import { HoldToRecord } from "../components/interview/HoldToRecord";
import { TypeAnswer } from "../components/interview/TypeAnswer";
import { ProgressDots } from "../components/interview/ProgressDots";
import { HistoryDrawer, type HistoryEntry } from "../components/interview/HistoryDrawer";

const ANSWER_HOLD_MS = 2200;

export default function SubjectSession() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  // Unified answer surface: one draft owned at this level. Voice (realtime
  // transcript) and typing both write here. User reviews + clicks Send (or ⌘+Enter).
  const [draft, setDraft] = useState("");
  const draftFromVoice = useRef(false);
  const onDraftChange = (next: string) => {
    if (next.trim().length === 0) draftFromVoice.current = false;
    setDraft(next);
  };

  const {
    uiState,
    currentQuestion,
    streamingText,
    questions,
    answers,
    terminationReason,
    error,
    bootstrap,
    submitAudioAnswer,
    submitTypedAnswer,
    done,
  } = useInterviewSession();

  const voice = useQuestionVoice("elevenlabs");
  const lastSpokenQuestionId = useRef<string | null>(null);

  const rt = useRealtimeTranscription({
    onFinal: ({ text }) => {
      // Realtime path: append the final transcript to the draft instead of
      // auto-submitting. User reviews + clicks Send.
      setDraft((prev) => (prev.trim().length > 0 ? `${prev} ${text}` : text));
      draftFromVoice.current = true;
    },
  });

  useEffect(() => {
    if (!getSubjectToken()) {
      navigate(`/i/${code}`, { replace: true });
    } else {
      void bootstrap();
    }
  }, [code, navigate, bootstrap]);

  useEffect(() => {
    if (!currentQuestion) return;
    if (lastSpokenQuestionId.current === currentQuestion.id) return;
    lastSpokenQuestionId.current = currentQuestion.id;
    void voice.speak(currentQuestion.text, { questionId: currentQuestion.id });
  }, [currentQuestion, voice]);

  // Brief in-page echo of the most recent answer.
  const [showingAnswer, setShowingAnswer] = useState(false);
  const [echoText, setEchoText] = useState<{ text: string; typed: boolean } | null>(null);
  const lastSeenAnswerCount = useRef(0);
  useEffect(() => {
    if (answers.length > lastSeenAnswerCount.current) {
      const newest = answers[answers.length - 1];
      lastSeenAnswerCount.current = answers.length;
      setEchoText({ text: newest.text, typed: newest.typed });
      setShowingAnswer(true);
      // Reset the draft surface for the next question.
      setDraft("");
      draftFromVoice.current = false;
      const t = window.setTimeout(() => {
        setShowingAnswer(false);
        setEchoText(null);
      }, ANSWER_HOLD_MS);
      return () => window.clearTimeout(t);
    }
  }, [answers]);

  const history: HistoryEntry[] = useMemo(() => {
    return questions.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      return { question: q.text, answer: a?.text ?? null, typed: a?.typed };
    });
  }, [questions, answers]);

  if (uiState === "terminated") {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="max-w-xl space-y-7">
          <h1 className="font-hand text-6xl text-ink-900">Thank you.</h1>
          <p className="text-ink-700 leading-relaxed text-lg">
            {terminationReason === "user_done"
              ? "You called it when it was right. Your transcript is saved."
              : "We've reached a good stopping point. Your transcript is saved."}
          </p>
          <p className="text-xs text-ink-300 uppercase tracking-[0.2em]">You can close this window</p>
        </div>
      </main>
    );
  }

  if (uiState === "error" && error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="max-w-md space-y-4">
          <h1 className="font-display text-3xl text-ink-900">Something interrupted us.</h1>
          <p className="text-ink-700">
            {error.kind === "token_expired"
              ? "Your session expired. Ask the person who invited you to send a fresh link."
              : error.kind === "superseded"
                ? "Looks like this interview is open in another tab. Continue there."
                : error.kind === "cost_cap_hit"
                  ? "We've reached our usage limit for now."
                  : "Try refreshing the page. If it keeps happening, reach out to the person who invited you."}
          </p>
        </div>
      </main>
    );
  }

  const maxQuestions = 12;
  const thinking =
    uiState === "thinking" || uiState === "bootstrapping" || uiState === "submitting_answer";
  const canAnswer = uiState === "reading_question" || uiState === "awaiting_answer";

  // Drive realtime transcription off the actual recording lifecycle. Avoids
  // the tap-toggle problem where pointer events fire repeatedly without the
  // recorder state actually changing.
  const handleRecordingChange = (recording: boolean) => {
    if (recording) void rt.start();
    else void rt.stop();
  };

  return (
    <main className="min-h-screen flex flex-col px-6 py-7 sm:py-10">
      <header className="flex items-center justify-between max-w-3xl w-full mx-auto">
        <div className="flex items-center gap-5">
          <ProgressDots total={maxQuestions} current={questions.length} />
          <HistoryDrawer entries={history} />
        </div>
        <button
          type="button"
          onClick={() => void done()}
          className="text-[10px] uppercase tracking-[0.22em] text-ink-300 hover:text-ink-500 transition-colors"
        >
          I'm done
        </button>
      </header>

      <section className="flex-1 flex flex-col items-center justify-center max-w-3xl w-full mx-auto py-8 sm:py-16">
        <QuestionAndAnswer
          questionText={currentQuestion?.text ?? null}
          streamingQuestion={streamingText}
          answerText={echoText?.text ?? null}
          questionLive={Boolean(streamingText) && !thinking}
          showingAnswer={showingAnswer}
          answerLabel={echoText?.typed ? "you wrote" : "you said"}
          onReplay={voice.replay}
          isSpeaking={voice.isSpeaking}
        />
        {thinking && !currentQuestion && (
          <div className="mt-8">
            <ThinkingHint />
          </div>
        )}
      </section>

      <footer className="max-w-3xl w-full mx-auto flex flex-col items-center gap-10 pb-6 sm:pb-10 min-h-[18rem]">
        {canAnswer && !showingAnswer ? (
          <>
            <HoldToRecord
              onSubmit={(blob, durationMs) => {
                // Realtime path is the happy case: rt.onFinal already pushed the
                // transcript into draft. Fall back to server-side transcribe-then-
                // submit ONLY when realtime gave us nothing AND we have a blob.
                if (!rt.final && blob.size > 0) {
                  void submitAudioAnswer(blob, durationMs);
                }
              }}
              liveTranscript={rt.status === "listening" ? rt.interim : undefined}
              onRecordingChange={handleRecordingChange}
            />
            <TypeAnswer
              value={draft}
              onValueChange={onDraftChange}
              onSubmit={(text) => {
                draftFromVoice.current = false;
                void submitTypedAnswer(text);
              }}
              placeholder="Press the mic, or write what comes to mind…"
              autoFocus={false}
            />
          </>
        ) : (
          <div className="h-40" aria-hidden="true" />
        )}
      </footer>

    </main>
  );
}

function ThinkingHint() {
  return (
    <p className="text-[10px] uppercase tracking-[0.22em] text-ink-300">
      One moment
    </p>
  );
}
